/**
 * The OtterAuth SDK, wrapped (04-architecture § 11.1, server 06-auth § 2).
 *
 * Two things this layer is for:
 *
 *  1. **Normalising the answer.** Every terminal step of every flow returns
 *     `LoginResponse`, a union of a session and the two detours. Each screen
 *     unpacking that union itself is how one of them ends up forgetting a
 *     branch, so it is unpacked once here into `AuthResult` and the screens
 *     feed that through `authReducer`.
 *  2. **Holding the configuration the docs fix.** `fingerprint: false` is not
 *     a preference: the fingerprint bootstrap loads `otter-fp.js` from the
 *     OtterAuth origin, and member and admin carry no third-party script but
 *     Stripe.js (§ 11.1). It also matches `fingerprintEnforcement = off`
 *     (A-035), which is why the challenge detour should never actually fire.
 */

import {
  OtterClient,
  isChallengeRequired,
  isTotpRequired,
  localStorageTokenStorage,
  memoryTokenStorage,
} from '@otterauth/client';
import type { LoginResponse, OAuthProvider, OtterUser, TokenStorage } from '@otterauth/client';
import type { AuthResult } from './index.js';

export type { OAuthProvider, OtterUser, TokenStorage };
/** Web uses the SDK's own `localStorage` storage (§ 11.1, accepted risk). */
export { localStorageTokenStorage, memoryTokenStorage };

/** The `otter` block of `GET /meta` (§ 5.4). */
export interface OtterRuntimeConfig {
  apiUrl: string;
  publishableKey: string;
}

export interface AuthConfig {
  /** The build-time fallback (§ 5.1); the value from `runtimeConfig` wins. */
  publishableKey: string;
  /** Fallback as well; the SDK defaults to the public host. */
  apiUrl?: string;
  /**
   * § 5.4 rule 1 — reads `otter` from `GET /meta`. The server's env is the one
   * place the key is configured; a build that was given none must still log in.
   */
  runtimeConfig?: () => Promise<OtterRuntimeConfig>;
  /** Mobile passes a SecureStore-backed storage, web the `localStorage` one. */
  storage?: TokenStorage;
  /** Fires when OtterAuth rejects the stored token, so the app can drop its mirror. */
  onSessionInvalidated?: () => void;
}

/**
 * What `POST /auth/totp/enroll` answers with.
 *
 * Three sources name the provisioning URI three different ways: the SDK's types
 * call it `qrCodeUrl`, OtterAuth's `llm.txt` calls it `otpauthUrl`, and the API
 * returns `uri`. The API wins — it is the one that has to be right — so the
 * field is read from all three and normalised once, here, rather than in the
 * screen that renders the QR code.
 */
export interface TotpEnrolment {
  secret: string;
  /** `otpauth://totp/...`, rendered locally as a QR code. */
  uri: string;
  recoveryCodes: string[];
}

/** The detour a code input is answering, so `submitCode` knows what to call. */
export type PendingCode = { kind: 'totp_required'; token: string } | { kind: 'challenge'; token: string };

export interface Auth {
  /** Step one of the e-mail OTP flow; the `methodId` must reach `verifyOtp`. */
  sendOtp(email: string): Promise<{ methodId: string }>;
  verifyOtp(methodId: string, code: string): Promise<AuthResult>;
  /** Redeems whichever detour came back. May itself return another detour. */
  submitCode(pending: PendingCode, code: string): Promise<AuthResult>;
  /** Web OAuth: returns the provider URL to send the browser to (§ 11.3). */
  startOAuth(provider: OAuthProvider, redirectUri: string): Promise<string>;
  completeOAuth(tempToken: string): Promise<AuthResult>;
  /** Native OAuth: the Apple/Google SDK already produced an id_token. */
  authenticateIdToken(provider: OAuthProvider, idToken: string): Promise<AuthResult>;
  /** § 11.5 — the caller does `POST /auth/logout` first, then this. */
  logout(): Promise<void>;
  me(): Promise<OtterUser>;
  /** The token the SDK currently holds, or `null`. */
  getToken(): string | null;
  /** Backoffice TOTP enrolment (06 § 4.2, D-034). */
  enrollTotp(): Promise<TotpEnrolment>;
  verifyTotpEnrollment(code: string): Promise<void>;
}

/**
 * `LoginResponse` → `AuthResult`.
 *
 * The order matters: both detours are tagged objects without a `sessionToken`,
 * so they are ruled out before the session case is assumed.
 */
export function toAuthResult(response: LoginResponse): AuthResult {
  if (isTotpRequired(response)) return { kind: 'totp_required', token: response.totpToken };
  if (isChallengeRequired(response)) return { kind: 'challenge', token: response.challengeToken };
  return { kind: 'session', sessionToken: response.sessionToken, userId: response.userId };
}

/**
 * `@otterauth/client@1.20` sends **no body** on the two authenticated POSTs it
 * has no arguments for — `/auth/logout` and `/auth/totp/enroll` — and OtterAuth
 * validates the body as required, so both answer `400 Validation failed`. The
 * consequences are not equal: a failed enrolment is visible, a failed logout is
 * not, and leaves an `ot_` token alive for its full 30 days after the user
 * believes they signed out.
 *
 * Both are issued directly with an empty object until the SDK is fixed. This is
 * the one place that bypasses it, and it does so with the SDK's own token.
 */
function emptyBodyPost(apiUrl: string | undefined, token: string | null, publishableKey: string) {
  const trimmed = (apiUrl ?? 'https://api.otterauth.com').replace(/\/$/, '');
  const base = /\/v\d+$/.test(trimmed) ? trimmed : `${trimmed}/v1`;

  return async (path: string): Promise<Record<string, unknown>> => {
    const response = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-api-key': publishableKey,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: '{}',
    });
    const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      throw Object.assign(new Error(String(json.error ?? response.status)), {
        status: response.status,
        code: typeof json.error === 'string' ? json.error : undefined,
      });
    }
    return json;
  };
}

export function createAuth(config: AuthConfig): Auth {
  const build = (apiUrl: string | undefined, publishableKey: string, storage?: TokenStorage) => ({
    apiUrl,
    publishableKey,
    otter: new OtterClient({
      publishableKey,
      ...(apiUrl ? { apiUrl } : {}),
      ...(storage ? { storage } : {}),
      fingerprint: false,
      ...(config.onSessionInvalidated ? { onSessionInvalidated: config.onSessionInvalidated } : {}),
    }),
  });

  let current = build(config.apiUrl, config.publishableKey, config.storage);
  // One storage for every client built here: the token must survive the swap.
  const storage = current.otter.storage;

  // § 5.4 rule 1 — every call that reaches OtterAuth waits for `/meta` once.
  // A failed read leaves the build-time fallback in place and is not
  // remembered, so the next call asks again.
  let resolved = !config.runtimeConfig;
  let loading: Promise<void> | null = null;
  let failure: unknown;
  const client = async () => {
    if (resolved) return current;
    loading ??= config.runtimeConfig!().then(
      (meta) => {
        current = build(meta.apiUrl, meta.publishableKey, storage);
        resolved = true;
      },
      (error: unknown) => {
        loading = null;
        failure = error;
      },
    );
    await loading;
    // No `/meta` and no fallback: the reason is that the server was not
    // reached, and a request without a key would only hide it behind
    // OtterAuth's `missing-or-invalid-publishable-key`.
    if (!resolved && !current.publishableKey) throw failure ?? new Error('meta unavailable');
    return current;
  };
  const otter = async () => (await client()).otter;

  return {
    sendOtp: async (email) => (await otter()).sendOtp({ method: 'email', email }),
    verifyOtp: async (methodId, code) => toAuthResult(await (await otter()).verifyOtp(methodId, code)),
    submitCode: async (pending, code) =>
      toAuthResult(
        pending.kind === 'totp_required'
          ? await (await otter()).authenticateTotp(pending.token, code)
          : await (await otter()).verifyChallenge(pending.token, code),
      ),
    startOAuth: async (provider, redirectUri) =>
      (await (await otter()).startOAuth(provider, redirectUri)).url,
    completeOAuth: async (tempToken) => toAuthResult(await (await otter()).completeOAuth(tempToken)),
    authenticateIdToken: async (provider, idToken) =>
      toAuthResult(await (await otter()).authenticateIdToken(provider, idToken)),
    logout: async () => {
      const token = storage.get();
      try {
        const { apiUrl, publishableKey } = await client();
        await emptyBodyPost(apiUrl, token, publishableKey)('/auth/logout');
      } finally {
        // Whatever OtterAuth said, this device must forget the token — the SDK
        // clears its storage here, and a failed revocation is not a reason to
        // leave a signed-out user holding one.
        storage.clear();
      }
    },
    me: async () => (await otter()).me(),
    getToken: () => storage.get(),
    enrollTotp: async () => {
      const { apiUrl, publishableKey } = await client();
      const raw = (await emptyBodyPost(apiUrl, storage.get(), publishableKey)('/auth/totp/enroll')) as {
        secret?: string;
        uri?: string;
        qrCodeUrl?: string;
        otpauthUrl?: string;
        recoveryCodes?: string[];
      };
      const uri = raw.uri ?? raw.qrCodeUrl ?? raw.otpauthUrl;
      if (!uri || !raw.secret) throw new Error('OtterAuth returned no TOTP provisioning URI');
      return { secret: raw.secret, uri, recoveryCodes: raw.recoveryCodes ?? [] };
    },
    verifyTotpEnrollment: async (code) => {
      await (await otter()).verifyTotpEnrollment(code);
    },
  };
}
